import { getUserTimezone } from '@occc-app/lib/date';
import { RetailerNetworkOfferMetricsOverviewSortBy, SortOrder } from '@occc/graphql';
import { useSortingParams } from '@occc-app/hooks/useSortingParams';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';

import {
  OFFER_PERFORMANCE_DEFAULT_PAGE_SIZE,
  OFFER_PERFORMANCE_AFTER_PARAM,
  OFFER_PERFORMANCE_BEFORE_PARAM,
  OFFER_PERFORMANCE_SORT_DIRECTION,
  OFFER_PERFORMANCE_SORT_FIELD,
} from '../retailer-network-overview-page.constants';
import { useRetailerNetworkUserSelector } from '../../retailer-network-context/use-retailer-network-user.selector';
import {
  useGetRetailerNetworkLocationDowntimeMetricsOverviewQuery,
  useGetRetailerNetworkLocationDowntimeMetricsQuery,
  useGetRetailerNetworkLocationOfferMetricsOverviewQuery,
  useGetRetailerNetworkTransactionMetricsOverviewQuery,
} from '../../../data-access/queries';

export const useRetailerNetworkOverviewController = (retailerNetworkUuid: string) => {
  const { isRetailerNetworkSiteUserRole, isRetailerNetworkUserRole } =
    useRetailerNetworkUserSelector();
  const [sortingParams, setSortingParams] =
    useSortingParams<RetailerNetworkOfferMetricsOverviewSortBy>({
      initialValues: {
        field: RetailerNetworkOfferMetricsOverviewSortBy.Redemptions,
        direction: 'desc',
      },
      sortFieldParamName: OFFER_PERFORMANCE_SORT_FIELD,
      sortDirectionParamName: OFFER_PERFORMANCE_SORT_DIRECTION,
      beforeParamName: OFFER_PERFORMANCE_BEFORE_PARAM,
      afterParamName: OFFER_PERFORMANCE_AFTER_PARAM,
    });
  const [paginationInfo, onPageChange] = usePaginationParams({
    beforeParamName: OFFER_PERFORMANCE_BEFORE_PARAM,
    afterParamName: OFFER_PERFORMANCE_AFTER_PARAM,
  });

  const timezone = getUserTimezone();

  const handleOfferPerformanceSort = () => {
    setSortingParams({
      field: RetailerNetworkOfferMetricsOverviewSortBy.Redemptions,
      direction: sortingParams?.direction === 'asc' ? 'desc' : 'asc',
    });
  };

  const {
    data: locationDowntimeMetricsData,
    error: locationDowntimeMetricsDataError,
    loading: isLocationDowntimeMetricsDataLoading,
  } = useGetRetailerNetworkLocationDowntimeMetricsQuery(
    {
      variables: {
        filter: {
          retailerNetworkUuidEquals: retailerNetworkUuid,
          timezone,
        },
      },
      fetchPolicy: 'cache-and-network',
    },
    !isRetailerNetworkSiteUserRole,
  );

  const {
    data: locationDowntimeMetricsOverview,
    error: locationDowntimeMetricsOverviewError,
    loading: isLocationDowntimeMetricsOverviewLoading,
  } = useGetRetailerNetworkLocationDowntimeMetricsOverviewQuery(
    {
      variables: {
        filter: {
          retailerNetworkUuidEquals: retailerNetworkUuid,
          timezone,
        },
      },
      fetchPolicy: 'cache-and-network',
    },
    !isRetailerNetworkUserRole,
  );

  const {
    data: transactionMetricsOverview,
    error: transactionMetricsOverviewError,
    loading: isTransactionMetricsOverviewLoading,
  } = useGetRetailerNetworkTransactionMetricsOverviewQuery({
    variables: {
      filter: {
        retailerNetworkUuidEquals: retailerNetworkUuid,
        timezone,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const {
    data: locationOfferMetricsOverview,
    error: locationOfferMetricsOverviewError,
    loading: isLocationOfferMetricsOverviewLoading,
    paginationProps: locationOfferMetricsOverviewPaginationProps,
  } = useGetRetailerNetworkLocationOfferMetricsOverviewQuery({
    filter: {
      retailerNetworkUuidEquals: retailerNetworkUuid,
      timezone,
    },
    sort: {
      field: sortingParams?.field ?? RetailerNetworkOfferMetricsOverviewSortBy.Redemptions,
      direction: sortingParams?.direction === 'asc' ? SortOrder.Asc : SortOrder.Desc,
    },
    pageSize: OFFER_PERFORMANCE_DEFAULT_PAGE_SIZE,
    paginationInfo,
    onPageChange,
  });

  return {
    isRetailerNetworkSiteUserRole,
    isRetailerNetworkUserRole,
    // Location Downtime Metrics
    locationDowntimeMetricsData,
    locationDowntimeMetricsDataError,
    isLocationDowntimeMetricsDataLoading,
    // Location Downtime Metrics Overview
    locationDowntimeMetricsOverview,
    locationDowntimeMetricsOverviewError,
    isLocationDowntimeMetricsOverviewLoading,
    // Transaction Metrics Overview
    transactionMetricsOverview,
    transactionMetricsOverviewError,
    isTransactionMetricsOverviewLoading,
    // Location Offer Metrics Overview
    locationOfferMetricsOverview,
    locationOfferMetricsOverviewError,
    isLocationOfferMetricsOverviewLoading,
    locationOfferMetricsOverviewPaginationProps,
    // Sorting
    sortingParams,
    handleOfferPerformanceSort,
    // Pagination
    paginationInfo,
    onPageChange,
  };
};
